import { createFileRoute } from '@tanstack/react-router';

import { AuthenticatedMobileShell } from '../components/AuthenticatedMobileShell.tsx';
import { InvoiceWorkspace } from '../components/InvoiceWorkspace.tsx';
import { validateInvoiceWorkspaceSearch } from '../components/invoiceWorkspaceSearch.ts';
import { useMe } from '../lib/auth.ts';

// File-based route for `/`. The Vite TanStack Router plugin reads this file
// and writes the matching entry into routeTree.gen.ts at build/dev time.
export const Route = createFileRoute('/')({
  validateSearch: validateInvoiceWorkspaceSearch,
  component: Home,
});

// Exported as a named component so tests (tests/unit/client.test.tsx) can
// render the auth-aware UI directly through a QueryClientProvider without
// also booting the router + generated route tree.
export function Home(): React.ReactElement {
  const me = useMe();

  // Three explicit visual states. We keep them as separate branches instead
  // of computing a combined flag so each render path is easy to read in the
  // tests and a future fourth state (e.g. server outage banner) drops in
  // without rewriting the others.
  if (me.isLoading) {
    return <CenteredMessage>Loading…</CenteredMessage>;
  }

  // Explicit `!= null` over a truthy check so the two falsy states stay
  // distinguishable in code: `null` is the explicit logged-out signal from
  // useMe, `undefined` is "query unresolved / errored". Both still fall
  // through to <LoginScreen/> below by design, but the branch now reads as
  // "we have authenticated data" rather than relying on JavaScript
  // truthiness coincidence (T6-R1-01).
  if (me.data != null) {
    return (
      <AuthenticatedMobileShell currentPath="/">
        <InvoiceWorkspace />
      </AuthenticatedMobileShell>
    );
  }

  // `data === null` (explicit 401) or query error — both collapse to the
  // login screen. Surfacing a transient `/api/me` error as a login screen
  // is the safer default for an owner-only app: the user can click "Sign
  // in with GitHub" and either succeed (cookie was fine, transient blip)
  // or re-authenticate.
  return <LoginScreen />;
}

function LoginScreen(): React.ReactElement {
  return (
    <CenteredFrame>
      <div className="flex flex-col items-center gap-6 rounded-2xl border border-line bg-paper-soft px-10 py-12 shadow-[0_18px_60px_-30px_rgba(31,27,21,0.35)]">
        <div className="flex flex-col items-center gap-2">
          <h1 className="font-serif text-4xl font-medium italic leading-tight text-ink">
            Invoice Generator
          </h1>
          <p className="max-w-xs text-balance text-sm text-ink-soft">
            A small, private workshop for your invoices. Sign in to continue.
          </p>
        </div>
        {/*
          Same-origin server route, plain top-frame navigation — no
          `target="_blank"` and no `rel="noopener"` needed. If a future
          screen ever links directly to an external OAuth provider in a
          new tab, that link MUST include `rel="noopener noreferrer"` to
          defend against window.opener attacks (T6-R1-02).
        */}
        <a
          href="/auth/github/login"
          className="inline-flex min-h-11 items-center justify-center rounded-full bg-ochre px-6 py-2.5 text-sm font-semibold text-paper-soft shadow-[0_8px_20px_-10px_rgba(176,119,43,0.8)] transition hover:bg-ochre-deep"
        >
          Sign in with GitHub
        </a>
      </div>
    </CenteredFrame>
  );
}

function CenteredFrame({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <div className="flex min-h-screen items-center justify-center px-6">
      <div className="flex flex-col items-center gap-4 text-center">{children}</div>
    </div>
  );
}

function CenteredMessage({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <div className="flex min-h-screen items-center justify-center px-6 text-sm text-ink-muted">
      {children}
    </div>
  );
}
